import React, { Fragment, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, Container } from "reactstrap";
import { useSelector } from "react-redux";
import { Briefcase, Eye, EyeOff, Lock } from "react-feather";

import Breadcrumb from "../common/breadcrumb";
import PillTabs from "../common/PillTabs";
import {
  Cols,
  Field,
  Section,
  hintStyle,
  inputStyle,
  primaryBtnStyle,
  secondaryBtnStyle,
  warnStyle,
  withDisabled,
} from "../common/FormKit";
import { C, reportingGlobalCSS } from "../reporting/reportingStyles";
import { createUser } from "../../utils/apiCalls";
import { USER_TYPE } from "../../constants/roles";

/**
 * Add User creates staff logins only: a merchant user or an operations user, always inside the
 * merchant picked in the header. Customers sign up in the apps; platform admins are not created
 * here (the API refuses them too). Staff sign in with email and password, so there is no phone.
 */

const ACCOUNT_TYPES = [
  {
    key: USER_TYPE.MERCHANT,
    label: "Merchant",
    description: "Full access to this merchant in the admin panel.",
  },
  {
    key: USER_TYPE.OPERATIONS,
    label: "Operations",
    description: "Read-only pages for this merchant; can adjust a customer's wallet and loyalty.",
  },
];

// Mirrors the API (AuthenticationControllerPolicy.register and util/passwordRules.js).
const NAME_RE = /^.{2,32}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_RE = /^[a-zA-Z0-9!@#$%^&*()_+\-=.]{8,32}$/;
const PASSWORD_HINT = "8–32 characters: letters, numbers and !@#$%^&*()_+-=.";

const EMPTY = { firstName: "", lastName: "", email: "", password: "" };

export function validateStaffUser(form) {
  const errors = {};
  if (!NAME_RE.test(form.firstName.trim())) errors.firstName = "Enter 2–32 characters.";
  if (form.lastName.trim() && !NAME_RE.test(form.lastName.trim())) errors.lastName = "Enter 2–32 characters, or leave it empty.";
  if (!EMAIL_RE.test(form.email.trim())) errors.email = "Enter a valid email address.";
  if (!PASSWORD_RE.test(form.password)) errors.password = PASSWORD_HINT;
  return errors;
}

const errorStyle = { ...hintStyle, color: "#DC2626" };

const AddUser = () => {
  const navigate = useNavigate();
  const { selectedMerchant, merchants = [] } = useSelector((state) => state.merchant || {});

  const [accountType, setAccountType] = useState(USER_TYPE.MERCHANT);
  const [form, setForm] = useState(EMPTY);
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  const merchantName = selectedMerchant
    ? merchants.find((m) => String(m.id) === String(selectedMerchant))?.name || `Merchant #${selectedMerchant}`
    : null;
  const errors = useMemo(() => validateStaffUser(form), [form]);
  const shownErrors = submitted ? errors : {};
  const type = ACCOUNT_TYPES.find((t) => t.key === accountType);

  if (localStorage.getItem("userType") !== USER_TYPE.ADMIN) {
    return (
      <Fragment>
        <Breadcrumb title="Add User" parent="Users" />
        <Container fluid>
          <Card>
            <CardBody style={{ textAlign: "center", padding: 20 }}>Only admins can create users.</CardBody>
          </Card>
        </Container>
      </Fragment>
    );
  }

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!selectedMerchant || Object.keys(errors).length || saving) return;

    const payload = {
      firstName: form.firstName.trim(),
      email: form.email.trim(),
      password: form.password,
      userType: accountType,
      MerchantId: Number(selectedMerchant),
    };
    // The API rejects an empty string here, so an unset last name is left out.
    if (form.lastName.trim()) payload.lastName = form.lastName.trim();

    setSaving(true);
    const created = await createUser(payload);
    setSaving(false);
    if (created) navigate("/users/list-user");
  };

  const fieldInput = (key, props = {}) => (
    <input
      aria-label={props["aria-label"]}
      style={{ ...inputStyle, ...(shownErrors[key] ? { borderColor: "#DC2626" } : {}) }}
      value={form[key]}
      onChange={set(key)}
      disabled={saving}
      {...props}
    />
  );

  return (
    <Fragment>
      <Breadcrumb title="Add User" parent="Users" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <form onSubmit={submit} noValidate style={{ maxWidth: 760, display: "flex", flexDirection: "column", gap: 16 }}>
          <Section
            icon={Briefcase}
            title="Account type"
            subtitle="Staff login for the admin panel. Customers sign up in the apps."
          >
            <PillTabs
              ariaLabel="Account type"
              tabs={ACCOUNT_TYPES.map(({ key, label }) => ({ key, label }))}
              value={accountType}
              onChange={setAccountType}
            />
            <span style={{ ...hintStyle, marginTop: -8 }}>{type.description}</span>

            <div
              data-testid="target-merchant"
              style={{
                padding: "10px 12px",
                borderRadius: 8,
                background: merchantName ? C.coralLight : C.orangeLight,
                fontSize: 13,
                color: C.text,
              }}
            >
              {merchantName ? (
                <>
                  Will be created in <strong>{merchantName}</strong>
                </>
              ) : (
                <span style={{ ...warnStyle, marginTop: 0 }}>Select a merchant from the header first.</span>
              )}
            </div>
          </Section>

          <Section icon={Lock} title="Login details" subtitle="The user signs in with this email and password.">
            <Cols min={220}>
              <Field label="First name" required>
                {fieldInput("firstName", { "aria-label": "First name", autoComplete: "off" })}
                {shownErrors.firstName && <span style={errorStyle}>{shownErrors.firstName}</span>}
              </Field>
              <Field label="Last name">
                {fieldInput("lastName", { "aria-label": "Last name", autoComplete: "off" })}
                {shownErrors.lastName && <span style={errorStyle}>{shownErrors.lastName}</span>}
              </Field>
            </Cols>

            <Field label="Email" required>
              {fieldInput("email", { "aria-label": "Email", type: "email", autoComplete: "off" })}
              {shownErrors.email && <span style={errorStyle}>{shownErrors.email}</span>}
            </Field>

            <Field label="Password" required>
              <div style={{ position: "relative" }}>
                {fieldInput("password", {
                  "aria-label": "Password",
                  type: showPassword ? "text" : "password",
                  autoComplete: "new-password",
                  style: { ...inputStyle, paddingRight: 40, ...(shownErrors.password ? { borderColor: "#DC2626" } : {}) },
                })}
                <button
                  type="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((v) => !v)}
                  style={{
                    position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                    border: "none", background: "transparent", cursor: "pointer", color: C.textLight, padding: 4,
                  }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <span style={shownErrors.password ? errorStyle : hintStyle}>{PASSWORD_HINT}</span>
            </Field>
          </Section>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button type="button" style={secondaryBtnStyle} onClick={() => navigate("/users/list-user")}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !selectedMerchant}
              style={withDisabled(primaryBtnStyle, saving || !selectedMerchant)}
            >
              {saving ? "Creating…" : `Create ${type.label.toLowerCase()} user`}
            </button>
          </div>
        </form>
      </Container>
    </Fragment>
  );
};

export default AddUser;
